import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import {
  useDisableTwoFactor, useEnableTwoFactor, useSession, useStartTwoFactor,
} from '../lib/session';
import { Alert, Button, Card, Field, HelpNote, SectionTitle } from './ui';

/** Four characters at a time: a thirty-two character run is unreadable. */
const grouped = (secret: string): string => secret.match(/.{1,4}/g)?.join(' ') ?? secret;

/**
 * Setting up the authenticator app.
 *
 * The code is shown as a QR first, because the phone is already in their hand
 * and scanning it is one action. The same secret stays underneath in text, for
 * a password manager on the same screen or a camera that will not focus.
 */
export function TwoFactor({ forced = false }: { forced?: boolean }) {
  const { t } = useI18n();
  const { data: session } = useSession();
  const start = useStartTwoFactor();
  const enable = useEnableTwoFactor();
  const disable = useDisableTwoFactor();

  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [removing, setRemoving] = useState(false);
  const [qr, setQr] = useState<string | null>(null);

  const uri = start.data?.uri;
  useEffect(() => {
    if (!uri) { setQr(null); return; }
    let alive = true;
    void QRCode.toDataURL(uri, {
      errorCorrectionLevel: 'M', margin: 1, width: 512,
      color: { dark: '#090a0c', light: '#f0f0f5' },
    }).then((url) => { if (alive) setQr(url); });
    return () => { alive = false; };
  }, [uri]);

  const errorText = useApiError();
  const error = [start.error, enable.error, disable.error]
    .find((e): e is ApiError => e instanceof ApiError);

  if (session?.twoFactorEnabled && !forced) {
    return (
      <Card>
        <SectionTitle action={<HelpNote text={t('twofa.help')} />}>{t('twofa.title')}</SectionTitle>
        {error && <div className="mb-3"><Alert>{errorText(error) ?? error.message}</Alert></div>}

        {!removing ? (
          <>
            <p className="text-sm text-ink-dim">{t('twofa.on')}</p>
            <Button variant="ghost" className="mt-3 w-full" onClick={() => setRemoving(true)}>
              {t('twofa.turnOff')}
            </Button>
          </>
        ) : (
          <>
            <Field
              label={t('twofa.yourPassword')}
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <div className="mt-3 flex gap-2">
              <Button
                variant="danger"
                className="flex-1"
                loading={disable.isPending}
                disabled={password.length === 0}
                onClick={() => disable.mutate(password, {
                  onSuccess: () => { setRemoving(false); setPassword(''); },
                })}
              >
                {t('twofa.turnOff')}
              </Button>
              <Button variant="ghost" onClick={() => { setRemoving(false); setPassword(''); }}>
                {t('common.cancel')}
              </Button>
            </div>
          </>
        )}
      </Card>
    );
  }

  return (
    <Card>
      <SectionTitle action={<HelpNote text={t('twofa.help')} />}>{t('twofa.title')}</SectionTitle>
      {error && <div className="mb-3"><Alert>{errorText(error) ?? error.message}</Alert></div>}

      {forced && <div className="mb-3"><Alert tone="warning">{t('twofa.required')}</Alert></div>}

      {!start.data ? (
        <Button
          // Secondary here: on the account page the password form above owns
          // the one filled button, and two of them compete.
          variant={forced ? 'primary' : 'ghost'}
          className="w-full"
          loading={start.isPending}
          onClick={() => start.mutate()}
        >
          {t('twofa.begin')}
        </Button>
      ) : (
        <>
          <p className="text-sm text-ink-dim">{t('twofa.scan')}</p>

          {qr && (
            <img
              src={qr}
              alt={t('twofa.qrAlt')}
              className="mx-auto mt-3 w-44 rounded-xl border border-line"
            />
          )}

          <p className="mt-3 text-xs text-ink-faint">{t('twofa.orType')}</p>
          <p className="tabular mt-1 select-all break-all rounded-xl border border-line bg-raised px-3.5 py-2.5 text-center text-xs tracking-[0.14em] text-ink-dim">
            {grouped(start.data.secret)}
          </p>

          <div className="mt-4">
            <Field
              label={t('twofa.codeFromApp')}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </div>

          <Button
            className="mt-3 w-full"
            loading={enable.isPending}
            disabled={code.trim().length < 6}
            onClick={() => enable.mutate(code.trim(), { onSuccess: () => setCode('') })}
          >
            {t('twofa.confirm')}
          </Button>
        </>
      )}
    </Card>
  );
}
